import { __assign, __awaiter, __generator } from "tslib";
import axios from 'axios';
import KvikaApiError from './ApiError';
import { ErrorDescription } from './Constants';
import { PlatformEnum } from './Types';
import { resolveArray, resolveType } from './Utils';
var KvikaClient = (function () {
    function KvikaClient(_a) {
        var apiUrl = _a.apiUrl, storeAuthToken = _a.storeAuthToken, appToken = _a.appToken, authToken = _a.authToken, onResponseHeaders = _a.onResponseHeaders, loginRequestToken = _a.loginRequestToken;
        this.requestConfig = {
            baseURL: '',
            responseType: 'json',
            headers: {
                'Content-Type': 'application/json',
            },
            withCredentials: true,
        };
        this.axiosInstance = axios.create(__assign(__assign({}, this.requestConfig), { baseURL: apiUrl }));
        this.axiosInstance.interceptors.response.use(function (response) {
            if (onResponseHeaders) {
                onResponseHeaders(response.headers);
            }
            return response;
        }, function (error) {
            var _a;
            if (onResponseHeaders && ((_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.headers)) {
                onResponseHeaders(error.response.headers);
            }
            return Promise.reject(error);
        });
        this.axiosInstance.interceptors.request.use(function (config) {
            var newConfig = __assign({}, config);
            if (authToken) {
                newConfig.headers.Authorization = "Bearer ".concat(authToken);
            }
            if (loginRequestToken) {
                newConfig.headers['X-Login-Request-Token'] = loginRequestToken;
            }
            newConfig.headers['X-Authorization-App'] = appToken;
            return newConfig;
        });
        this.storeAuthToken = storeAuthToken;
    }
    KvikaClient.prototype.getSurvey = function (surveyType, ssn) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.get("/kyc/surveys/".concat(surveyType, "/").concat(ssn))];
                    case 1:
                        response = _a.sent();
                        return [2, resolveType(response.data, 'id')];
                    case 2:
                        error_1 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.GetSurveyError, error_1);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.login = function (_a) {
        var identifier = _a.identifier, pushToken = _a.pushToken, _b = _a.platform, platform = _b === void 0 ? PlatformEnum.WEB : _b, verificationCode = _a.verificationCode, customMessage = _a.customMessage;
        return __awaiter(this, void 0, void 0, function () {
            var request, response_1, error_2;
            var _this = this;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        request = { identifier: identifier, pushToken: pushToken, verificationCode: verificationCode, customMessage: customMessage };
                        _c.label = 1;
                    case 1:
                        _c.trys.push([1, 3, , 4]);
                        this.axiosInstance.defaults.headers.common['X-Platform'] = platform;
                        return [4, this.axiosInstance.post("/auth/login", request)];
                    case 2:
                        response_1 = _c.sent();
                        return [2, resolveType(response_1.data, 'authorizationToken', function () {
                                _this.axiosInstance.defaults.headers.common.Authorization = "Bearer ".concat(response_1.data.authorizationToken);
                                _this.storeAuthToken(response_1.data.authorizationToken);
                                delete _this.axiosInstance.defaults.headers.common['X-Platform'];
                            })];
                    case 3:
                        error_2 = _c.sent();
                        throw new KvikaApiError(ErrorDescription.LoginError, error_2);
                    case 4: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.getLastAnswers = function (surveyType, ssn) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_3;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.get("/kyc/surveys/".concat(surveyType, "/answers/").concat(ssn))];
                    case 1:
                        response = _a.sent();
                        return [2, resolveType(response.data, 'subjectSSN')];
                    case 2:
                        error_3 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.LastAnswersError, error_3);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.submitSurveyResults = function (surveyType, ssn, surveyData) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_4;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.post("/kyc/surveys/".concat(surveyType, "/answers/").concat(ssn), surveyData)];
                    case 1:
                        response = _a.sent();
                        return [2, resolveType(response.data, 'id')];
                    case 2:
                        error_4 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.SubmitSurveyError, error_4);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.getCompanies = function () {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_5;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.get("/companies")];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_5 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.GetCompaniesError, error_5);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.getCompaniesAnswerInfo = function (surveyType, allowEmpty) {
        if (allowEmpty === void 0) { allowEmpty = false; }
        return __awaiter(this, void 0, void 0, function () {
            var response, error_6;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.get("/kyc/companies/".concat(surveyType))];
                    case 1:
                        response = _a.sent();
                        return [2, resolveArray(response.data, 'id', allowEmpty)];
                    case 2:
                        error_6 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.GetCompaniesAnswerInfoError, error_6);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.getCountries = function () {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_7;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.get("/countries")];
                    case 1:
                        response = _a.sent();
                        return [2, resolveArray(response.data, 'id')];
                    case 2:
                        error_7 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.GetCountriesError, error_7);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.getDerivativesOnboarding = function (ssn) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_8;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.get("/onboarding/derivatives/".concat(ssn))];
                    case 1:
                        response = _a.sent();
                        return [2, resolveType(response.data, 'isComplete')];
                    case 2:
                        error_8 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.GetOnboardingError, error_8);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.putDerivativesOnboarding = function (payload) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_9;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.put("/onboarding/derivatives", payload)];
                    case 1:
                        response = _a.sent();
                        return [2, resolveType(response.data, 'isComplete')];
                    case 2:
                        error_9 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.PutOnboardingError, error_9);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.getCapitalMarketsOnboarding = function (ssn) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_10;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.get("/onboarding/capital_markets/".concat(ssn))];
                    case 1:
                        response = _a.sent();
                        return [2, resolveType(response.data, 'isComplete')];
                    case 2:
                        error_10 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.GetOnboardingError, error_10);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.putCapitalMarketsOnboarding = function (payload) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_11;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.put("/onboarding/capital_markets", payload)];
                    case 1:
                        response = _a.sent();
                        return [2, resolveType(response.data, 'isComplete')];
                    case 2:
                        error_11 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.PutOnboardingError, error_11);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.signDocument = function (documentId) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_12;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.post("/documents/sign/".concat(documentId))];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.status)];
                    case 2:
                        error_12 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.SignDocumentError, error_12);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.uploadDocument = function (ssn, type, file) {
        return __awaiter(this, void 0, void 0, function () {
            var formData, response, error_13;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        formData = new FormData();
                        formData.append('file', file);
                        return [4, this.axiosInstance.post("/documents/".concat(type, "/upload/").concat(ssn), formData, {
                                headers: {
                                    'Content-Type': 'multipart/form-data',
                                },
                            })];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_13 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.UploadDocumentError, error_13);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.clearAnswersOnStaging = function () {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_14;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.delete("/debug/reset_user")];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_14 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.ClearAnswersError, error_14);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.clearCompanyAnswersOnStaging = function (ssn) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_15;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.delete("/debug/reset_company/".concat(ssn))];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_15 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.ClearCompanyAnswerError, error_15);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.updateUserEmail = function (email) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_16;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.post('/users/email', { email: email })];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_16 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.UpdateEmailError, error_16);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.updateUserAddress = function (address) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_17;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.put('/users/addresses', {
                                address: address.address,
                                city: address.city,
                                postalCode: address.postalCode,
                                countryId: address.country.id,
                            })];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_17 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.UpdateAddressError, error_17);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.verifyEmail = function (token) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_18;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.put("/users/verify_email/".concat(token))];
                    case 1:
                        response = _a.sent();
                        return [2, response.data];
                    case 2:
                        error_18 = _a.sent();
                        throw new KvikaApiError('Error verifying email', error_18);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.postPurchaseRequest = function (purchaseRequest) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_19;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.post('/investments/funds/purchase_request', purchaseRequest)];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_19 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.PostPurchaseRequestError, error_19);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.getAppropriateFunds = function (ssn) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_20;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.get("/investments/funds/appropriate/".concat(ssn))];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_20 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.GetAppropriateFundsError, error_20);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.postRelinquishRights = function (relinquishRightsData) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_21;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.post("/investments/funds/relinquish_rights", relinquishRightsData)];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_21 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.PostRelinquishRightsError, error_21);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.getBankingProducts = function () {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_22;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.get('/banking/products')];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_22 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.GetBankProductsError, error_22);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.putCompanyAddress = function (address, companySsn) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_23;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.put("/companies/".concat(companySsn, "/addresses"), {
                                address: address.address,
                                city: address.city,
                                postalCode: address.postalCode,
                                countryId: address.country.id,
                            })];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_23 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.PutCompanyAddressError, error_23);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.getRaise = function () {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_24;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.get("/debug/raise")];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_24 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.GetRaiseError, error_24);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.getTimeout = function () {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_25;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.get("/debug/timeout")];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_25 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.GetTimeoutErorr, error_25);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.getChildren = function (surveyType) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_26;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.get("/kyc/children/".concat(surveyType))];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_26 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.GetChildrenError, error_26);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.getStrings = function () {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_27;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.get("/strings")];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_27 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.GetStringsError, error_27);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.getGeneralBankingOnboarding = function (ssn) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_28;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.get("/onboarding/general_banking/".concat(ssn))];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_28 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.GetGeneralBankingOnboardingError, error_28);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.putGeneralBankingOnboarding = function (data) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_29;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.put('/onboarding/general_banking', data)];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_29 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.PutGeneralBankingOnboardingError, error_29);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.putGeneralBankingOnboardingPin = function (ssn, data) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_30;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.put("/onboarding/general_banking/pin/".concat(ssn), data)];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_30 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.PutGeneralBankingOnboardingError, error_30);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.getCurrentAuthSession = function () {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_31;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.get("/auth/sessions/current")];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_31 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.GetCurrentAuthSessionError, error_31);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.postExchangeToken = function () {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_32;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.post("/auth/sessions/exchange")];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_32 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.GetCurrentAuthSessionError, error_32);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.getOnboardingBlockers = function (ssn, onboardingType) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_33;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.get("/onboarding/blockers/".concat(onboardingType, "/").concat(ssn))];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_33 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.GetOnboardingBlockersError, error_33);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.getPrivateBankingOnboarding = function (ssn) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_34;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.get("/onboarding/private_banking/".concat(ssn))];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_34 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.GetPrivateBankingOnboardingError, error_34);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.putPrivateBankingOnboarding = function (data) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_35;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.put('/onboarding/private_banking', data)];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_35 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.PutPrivateBankingOnboardingError, error_35);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.postOnboardingPin = function (data) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_36;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.post('/onboarding/pin', data)];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_36 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.PostOnboardingPinError, error_36);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.postUsersTerms = function () {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_37;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.post('/users/terms')];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_37 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.PostUsersTerms, error_37);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.postDebugPortfoliosWithSSN = function (ssn) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_38;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.post("/debug/portfolios/".concat(ssn))];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_38 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.PostDebugPortfoliosWithSSNError, error_38);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.logout = function () {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_39;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.delete("/auth/logout")];
                    case 1:
                        response = _a.sent();
                        delete this.axiosInstance.defaults.headers.common.Authorization;
                        if (response.status === 200) {
                            return [2, Promise.resolve(response.status)];
                        }
                        throw new Error(ErrorDescription.LogoutError);
                    case 2:
                        error_39 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.LogoutError, error_39);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.getPortfolioTransactions = function (portfolioId) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_40;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.get("/investments/portfolios/".concat(portfolioId, "/transactions"))];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_40 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.GetPortfolioTransactionsError, error_40);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.getUserDetails = function () {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_41;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.get("/users/details")];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_41 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.GetUserDetailsError, error_41);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.getInvestmentUsers = function () {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_42;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.get("/investments/users")];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_42 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.GetInvestmentUserError, error_42);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.postSelectedInvestmentUser = function (user) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_43;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.post("/investments/users/selected", user)];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_43 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.PostSelectedInvestmentUserError, error_43);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.getPortfolioAccessForSelectedUser = function () {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_44;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.get("/investments/portfolios/access")];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_44 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.GetPortoliosForSelectedUserError, error_44);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.getInstrumentPriceHistory = function (symbol, startDate, endDate) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_45;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.get("/marketdata/instruments/".concat(symbol), {
                                params: {
                                    start_date: startDate,
                                    end_date: endDate,
                                },
                            })];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_45 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.GetInstrumentPriceHistoryError, error_45);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.getPortfoliosPerformance = function (ppid) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_46;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.get("/investments/portfolios/performance/".concat(ppid))];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_46 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.GetPortfoliosPerformanceError, error_46);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.getDerivativesAccessForSelectedUser = function () {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_47;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.get("/investments/derivatives/access")];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_47 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.GetDerivativesAccessError, error_47);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.getForwardContractsSummary = function (ssn) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_48;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.get("/investments/derivatives/forward_contracts/contracts/".concat(ssn, "/summary"))];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_48 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.GetForwardContractsSummaryError, error_48);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.getForwardContracts = function (ssn) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_49;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.get("/investments/derivatives/forward_contracts/contracts/".concat(ssn))];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_49 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.GetForwardContractsError, error_49);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.getPortfoliosPerformanceTransactions = function (ppid, page, pageSize) {
        if (page === void 0) { page = 1; }
        if (pageSize === void 0) { pageSize = 100; }
        return __awaiter(this, void 0, void 0, function () {
            var response, error_50;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.get("/investments/portfolios/performance/".concat(ppid, "/transactions"), {
                                params: {
                                    page: page,
                                    pageSize: pageSize,
                                },
                            })];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_50 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.GetPortfolioTransactionsError, error_50);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.getSelectedInvestmentUser = function () {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_51;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.get("/investments/users/selected")];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_51 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.GetSelectedInvestmentUserError, error_51);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.postStartLogin = function (data, platform) {
        if (platform === void 0) { platform = PlatformEnum.WEB; }
        return __awaiter(this, void 0, void 0, function () {
            var response, error_52;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        this.axiosInstance.defaults.headers.common['X-Platform'] = platform;
                        return [4, this.axiosInstance.post("/auth/login/init", data)];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_52 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.PostStartLoginError, error_52);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.getLoginStatus = function (identifier) {
        return __awaiter(this, void 0, void 0, function () {
            var response_2, error_53;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.get("/auth/login/verify?identifier=".concat(identifier))];
                    case 1:
                        response_2 = _a.sent();
                        return [2, resolveType(response_2.data, 'authorizationToken', function () {
                                _this.axiosInstance.defaults.headers.common.Authorization = "Bearer ".concat(response_2.data.authorizationToken);
                                _this.storeAuthToken(response_2.data.authorizationToken);
                                delete _this.axiosInstance.defaults.headers.common['X-Platform'];
                            })];
                    case 2:
                        error_53 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.GetLoginStatusError, error_53);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.sendEmail = function (messageData) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_54;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.post("/contact", messageData)];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_54 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.PostEmailError, error_54);
                    case 3: return [2];
                }
            });
        });
    };
    KvikaClient.prototype.getInstrumentDetail = function (symbol) {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_55;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4, this.axiosInstance.get("/marketdata/instruments/".concat(symbol, "/detail"))];
                    case 1:
                        response = _a.sent();
                        return [2, Promise.resolve(response.data)];
                    case 2:
                        error_55 = _a.sent();
                        throw new KvikaApiError(ErrorDescription.GetInstrumentDetailError, error_55);
                    case 3: return [2];
                }
            });
        });
    };
    return KvikaClient;
}());
export default KvikaClient;
//# sourceMappingURL=ApiClient.js.map